var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector, Store } from '@ngxs/store';
import { HistoryStateModel } from './history.state.model';
import { AddHistory, Undo, Redo, ResetHistory, CacheChapterSynchronization, RevertChapterSynchronization, ClearHistory, RemoveChapterSynchronization, CopyChapterSynchronization } from './history.actions';
import { HistoryAction } from '../models/history-action.enum';
import { ToggleVisibleNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { EngineService } from 'src/app/engine/engine.service';
import { SetChapter } from 'src/app/story/state/chapters/story.chapters.actions';
import { ChapterService } from 'src/app/story/chapter.service';
const initialState = {
    histories: [],
    current: -1,
    cache: {}
};
let HistoryState = class HistoryState {
    constructor(store, engineService, chapterService) {
        this.store = store;
        this.engineService = engineService;
        this.chapterService = chapterService;
    }
    /**
     * Selectors
     */
    static getHistories(state) {
        return state.histories;
    }
    static getCurrent(state) {
        return state.current;
    }
    /**
     * Actions
     */
    addHistory(context, action) {
        // tslint:disable-next-line:prefer-const
        let { current, histories } = context.getState();
        // When recording history, eliminate all future events
        histories = histories.slice(0, current + 1);
        // Limit history to 100 entries to prevent memory overflow
        if (histories.length >= 100) {
            histories.unshift();
        }
        histories.push(action.history);
        // Update current index
        current = histories.length - 1;
        context.patchState({
            histories: [...histories],
            current: current
        });
    }
    redo(context, action) {
        const histories = [...context.getState().histories];
        const current = context.getState().current + 1;
        const history = histories[current];
        if (!history) {
            return;
        }
        switch (history.action) {
            case HistoryAction.SetNodeVisible:
                this.toggleVisibleNodes(context, history);
                break;
        }
        context.patchState({
            current: current
        });
    }
    undo(context, action) {
        const histories = [...context.getState().histories];
        const current = context.getState().current - 1;
        // No undos
        if (!histories.length || current < 0) {
            return;
        }
        const history = histories[current];
        switch (history.action) {
            case HistoryAction.SetNodeVisible:
                this.toggleVisibleNodes(context, history);
                break;
        }
        context.patchState({
            current: current
        });
    }
    resetHistory(context, action) {
        context.setState(initialState);
    }
    ClearHistory(context, action) {
        context.patchState({
            histories: [],
            current: -1,
        });
    }
    toggleVisibleNodes(context, history) {
        const nodes = Object.values(history.value);
        const visibleNodes = nodes
            .filter(node => node.boolState.shown);
        const hiddenNodes = nodes
            .filter(node => !node.boolState.shown);
        context.dispatch(new ToggleVisibleNodes(visibleNodes, true, false));
        context.dispatch(new ToggleVisibleNodes(hiddenNodes, false, false));
    }
    cacheChapterSynchronization(context, action) {
        const { chapterId, cacheId } = action;
        const cacheKey = this.getCacheKey(cacheId, chapterId);
        const cache = context.getState().cache;
        // When readOnly is flagged, don't overwrite pre-existing values
        if (action.readOnly) {
            const synchronization = cache[cacheKey];
            if (synchronization) {
                return;
            }
        }
        cache[cacheKey] = Object.assign({}, action.synchronization);
        context.patchState({
            cache: Object.assign({}, cache)
        });
    }
    revertChapterSynchronization(context, action) {
        // Access the cache
        const { chapterId, cacheId } = action;
        const synchronization = this.getSynchronization(context, cacheId, chapterId);
        if (synchronization) {
            // Revert the chapter synchronization
            let chapter = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
                .find(_chapter => _chapter.id === chapterId);
            chapter = Object.assign({}, chapter);
            chapter.synchronization = synchronization;
            // Update the engine, which should then propagate back to the other states
            this.engineService.setGraphState(chapter.synchronization);
            // Update state and engine
            const index = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
                .findIndex(_chapter => chapter.id === _chapter.id);
            this.store.dispatch(new SetChapter(chapter));
            this.chapterService.addChapterToEngine(chapter, index);
        }
    }
    removeChapterSynchronization(context, action) {
        const cacheKey = this.getCacheKey(action.cacheId, action.chapterId);
        const cache = Object.assign({}, context.getState().cache);
        delete cache[cacheKey];
        context.patchState({
            cache: Object.assign({}, cache)
        });
    }
    copyChapterSynchronization(context, action) {
        const synchronization = this.getSynchronization(context, action.sourceCacheId, action.sourceChapterId);
        context.dispatch(new CacheChapterSynchronization(action.targetCacheId, action.targetChapterId, synchronization));
    }
    getSynchronization(context, cacheId, chapterId) {
        const cacheKey = this.getCacheKey(cacheId, chapterId);
        const cache = context.getState().cache;
        return cache[cacheKey];
    }
    getCacheKey(cacheId, chapterId) {
        return `${cacheId}_${chapterId}`;
    }
};
__decorate([
    Action(AddHistory),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, AddHistory]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "addHistory", null);
__decorate([
    Action(Redo),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Redo]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "redo", null);
__decorate([
    Action(Undo),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Undo]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "undo", null);
__decorate([
    Action(ResetHistory),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ResetHistory]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "resetHistory", null);
__decorate([
    Action(ClearHistory),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ClearHistory]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "ClearHistory", null);
__decorate([
    Action(CacheChapterSynchronization),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CacheChapterSynchronization]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "cacheChapterSynchronization", null);
__decorate([
    Action(RevertChapterSynchronization),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RevertChapterSynchronization]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "revertChapterSynchronization", null);
__decorate([
    Action(RemoveChapterSynchronization),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RemoveChapterSynchronization]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "removeChapterSynchronization", null);
__decorate([
    Action(CopyChapterSynchronization),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CopyChapterSynchronization]),
    __metadata("design:returntype", void 0)
], HistoryState.prototype, "copyChapterSynchronization", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [HistoryStateModel]),
    __metadata("design:returntype", void 0)
], HistoryState, "getHistories", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [HistoryStateModel]),
    __metadata("design:returntype", void 0)
], HistoryState, "getCurrent", null);
HistoryState = __decorate([
    State({
        name: 'history',
        defaults: initialState
    }),
    __metadata("design:paramtypes", [Store,
        EngineService,
        ChapterService])
], HistoryState);
export { HistoryState };
